import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
    LayoutDashboard, 
    Users, 
    Calendar, 
    Star, 
    DollarSign, 
    UsersRound,
    LogOut,
    Menu,
    X,
    FileText,
    ExternalLink,
    BarChart3,
    UserCog,
    Newspaper,
    CalendarPlus,
    Mail,
    BookUser
} from 'lucide-react';
import { useState } from 'react';

// New logo image URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png";

const Layout = () => {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const navItems = [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/analytics', label: 'Site Analytics', icon: BarChart3 },
        { path: '/members', label: 'Members', icon: Users },
        { path: '/contacts', label: 'Contacts', icon: BookUser },
        { path: '/committees', label: 'Committees', icon: UsersRound },
        { path: '/events', label: 'Events & RSVP', icon: Calendar },
        { path: '/member-events', label: 'Member Events', icon: CalendarPlus },
        { path: '/newsletters', label: 'Newsletters', icon: Newspaper },
        { path: '/bulk-email', label: 'Bulk Email', icon: Mail },
        { path: '/spotlights', label: 'Brother Spotlights', icon: Star },
        { path: '/donations', label: 'Donations', icon: DollarSign },
        { path: '/admin-management', label: 'Admin Management', icon: UserCog },
    ];

    return (
        <div className="min-h-screen bg-[#3D4435] flex">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/70 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static inset-y-0 left-0 z-50
                w-64 bg-[#4A5240] border-r border-[#5A6350]
                transform transition-transform duration-300 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 border-b border-[#5A6350]">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <img src={LOGO_URL} alt="Recovery Room Logo" className="w-8 h-8 object-contain" />
                                <div>
                                    <h1 className="text-lg font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                                    <p className="text-xs text-[#666]">Admin Portal</p>
                                </div>
                            </div>
                            <button 
                                className="lg:hidden p-2 hover:bg-[#5A6350] rounded-lg"
                                onClick={() => setSidebarOpen(false)}
                            >
                                <X className="w-5 h-5 text-[#A0A0A0]" />
                            </button>
                        </div>
                    </div>

                    {/* Back to Site Link */}
                    <div className="px-4 py-3 border-b border-[#5A6350]">
                        <a
                            href="/join"
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-[#C9A227] hover:bg-[#C9A227]/10 transition-colors"
                            data-testid="back-to-site-link"
                        >
                            <ExternalLink className="w-4 h-4" />
                            <span className="text-sm font-medium">Back to Main Site</span>
                        </a>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => `
                                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                                    ${isActive 
                                        ? 'bg-[#C9A227]/10 text-[#C9A227] font-medium' 
                                        : 'text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white'
                                    }
                                `}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>

                    {/* Admin info & Logout */}
                    <div className="p-4 border-t border-[#5A6350]">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-[#C9A227] flex items-center justify-center text-[#3D4435] font-medium">
                                {admin?.name?.charAt(0).toUpperCase() || 'A'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
                                <p className="text-xs text-[#666] truncate">{admin?.email}</p>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            className="w-full border-[#5A6350] text-[#A0A0A0] hover:text-red-400 hover:border-red-400/50 bg-transparent"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Top header */}
                <header className="sticky top-0 z-30 px-6 py-4 border-b border-[#5A6350] bg-[#3D4435]/90 backdrop-blur-lg">
                    <div className="flex items-center justify-between">
                        <button 
                            className="lg:hidden p-2 hover:bg-[#5A6350] rounded-lg"
                            onClick={() => setSidebarOpen(true)}
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="w-6 h-6 text-[#A0A0A0]" />
                        </button>
                        <div className="hidden lg:block" />
                        <p className="text-sm text-[#666]">
                            <span className="hidden sm:inline">Brotherhood. Growth. Purpose.</span>
                        </p>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default Layout;
